import { useState } from "react";
import { Building2, Wrench, Key, LogOut, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import api from "../lib/api";

const LandlordPortalPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", access_code: "" });
  
  const [landlordData, setLandlordData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const data = await api.landlordPortalAccess(loginForm.email, loginForm.access_code);
      setLandlordData(data.landlord);
      setProperties(data.properties);
      setMaintenanceRequests(data.maintenance_requests);
      setIsAuthenticated(true);
      toast.success("Welcome to your landlord portal");
    } catch (error) {
      toast.error("Invalid email or access code");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLandlordData(null);
    setProperties([]);
    setMaintenanceRequests([]);
    setLoginForm({ email: "", access_code: "" });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-off-white py-20" data-testid="landlord-portal-login">
        <div className="max-w-md mx-auto px-4">
          <Card className="border-slate-200">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-navy/10 flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-navy" />
              </div>
              <CardTitle className="text-2xl font-heading">Landlord Portal</CardTitle>
              <p className="text-slate-500 text-sm">Access your property reports and maintenance updates</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Email Address</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="landlord-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Access Code</label>
                  <Input
                    type="text"
                    placeholder="Enter your access code"
                    value={loginForm.access_code}
                    onChange={(e) => setLoginForm({ ...loginForm, access_code: e.target.value })}
                    required
                    data-testid="landlord-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light"
                  disabled={loginLoading}
                  data-testid="landlord-login-btn"
                >
                  {loginLoading ? "Accessing..." : "Access Portal"}
                </Button>
              </form>
              <p className="text-xs text-slate-500 mt-4 text-center">
                Your access code was provided by Keystone Properties. Contact us if you need assistance.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white py-8" data-testid="landlord-portal-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-navy">
              Welcome, {landlordData?.name}
            </h1>
            <p className="text-slate-600">Your property management dashboard</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-slate-300">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-navy" />
                </div>
                <div>
                  <div className="text-2xl font-heading font-bold text-navy">{properties.length}</div>
                  <div className="text-sm text-slate-500">Managed Properties</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <div className="text-2xl font-heading font-bold text-navy">
                    {maintenanceRequests.filter(m => m.status !== "resolved").length}
                  </div>
                  <div className="text-sm text-slate-500">Open Maintenance</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-heading font-bold text-navy">
                    {properties.filter(p => p.available === false).length}
                  </div>
                  <div className="text-sm text-slate-500">Currently Let</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Properties Section */}
        <Card className="border-slate-200 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Your Properties
            </CardTitle>
          </CardHeader>
          <CardContent>
            {properties.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((property) => (
                    <TableRow key={property.id}>
                      <TableCell>
                        <div className="font-medium">{property.title}</div>
                        <div className="text-sm text-slate-500">{property.address}</div>
                      </TableCell>
                      <TableCell>{property.location}</TableCell>
                      <TableCell>{formatPrice(property.price)}/mo</TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary"
                          className={property.available ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}
                        >
                          {property.available ? "Available" : "Let"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No properties assigned to your account yet.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Maintenance Requests Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Maintenance Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {maintenanceRequests.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {maintenanceRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="max-w-[200px] truncate">{request.property_address}</TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="truncate">{request.issue_description}</div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary"
                          className={
                            request.urgency === "emergency" ? "bg-red-100 text-red-800" :
                            request.urgency === "urgent" ? "bg-orange-100 text-orange-800" :
                            "bg-blue-100 text-blue-800"
                          }
                        >
                          {request.urgency === "emergency" && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {request.urgency}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary"
                          className={
                            request.status === "resolved" ? "bg-green-100 text-green-800" :
                            request.status === "in_progress" ? "bg-yellow-100 text-yellow-800" :
                            "bg-slate-100 text-slate-800"
                          }
                        >
                          {request.status === "resolved" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                          {request.status === "in_progress" && <Clock className="w-3 h-3 mr-1" />}
                          {request.status.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(request.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No maintenance requests for your properties.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-8 p-6 bg-navy/5 rounded-lg text-center">
          <p className="text-slate-600 mb-2">Need assistance with your properties?</p>
          <p className="text-navy font-medium">
            Contact Keystone Properties: <a href="tel:01924 520163" className="text-gold hover:underline">01924 520163</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandlordPortalPage;
